/**
 * Per-form status used to drive the batch-import collapsible border colour.
 *
 *   neutral  → grey   (pristine, nothing entered)
 *   progress → yellow (dirty, not yet valid, no surfaced errors)
 *   error    → red    (validation errors surfaced)
 *   valid    → green  (form passes its schema)
 */
export type ProductFormStatus = "neutral" | "progress" | "error" | "valid";

export interface ProductFormStatusInput {
  isValid: boolean;
  isDirty: boolean;
  hasErrors: boolean;
}

/** Derives the status enum from a form's validity / dirty / error signals. */
export function deriveProductFormStatus({ isValid, isDirty, hasErrors }: ProductFormStatusInput): ProductFormStatus {
  if (isValid) return "valid";
  if (hasErrors) return "error";
  if (isDirty) return "progress";
  return "neutral";
}

/** Maps a status to the Tailwind border class for the collapsible container. */
export function getProductFormBorderClass(status: ProductFormStatus): string {
  switch (status) {
    case "valid":
      return "border-green-500";
    case "error":
      return "border-red-500";
    case "progress":
      return "border-yellow-400";
    default:
      return "border-border";
  }
}

/** Maps a status to the Tailwind background class for the collapsible header dot. */
export function getProductFormDotClass(status: ProductFormStatus): string {
  switch (status) {
    case "valid":
      return "bg-green-500";
    case "error":
      return "bg-red-500";
    case "progress":
      return "bg-yellow-400";
    default:
      return "bg-muted-foreground";
  }
}
